import axios, { AxiosInstance, AxiosRequestConfig } from "axios";

// Token getter function - will be set by MsalProvider
let tokenGetter: (() => Promise<string | null>) | null = null;

export function setTokenGetter(getter: () => Promise<string | null>) {
  tokenGetter = getter;
}

// Placeholder for future use - currently not implemented
export function setSignOutHandler(_handler: () => void) {
  // TODO: Implement auto-logout on 401
}

const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - inject auth token
api.interceptors.request.use(
  async (config) => {
    if (tokenGetter) {
      const token = await tokenGetter();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('API request without token - user may not be authenticated');
      }
    } else {
      console.warn('Token getter not set - ApiSetup may not have initialized');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Log the error for debugging - don't auto-logout to avoid redirect loops
      console.error('401 Unauthorized - Check backend token validation configuration');
    }
    return Promise.reject(error);
  }
);

// Backend response wrapper type
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

// Helper to unwrap backend response format
function unwrapResponse<T>(responseData: unknown): T {
  // Check if response has the backend wrapper format
  if (
    responseData &&
    typeof responseData === 'object' &&
    'success' in responseData &&
    'data' in responseData
  ) {
    return (responseData as ApiResponse<T>).data;
  }
  // Return as-is if not wrapped
  return responseData as T;
}

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.get(url, config);
  return unwrapResponse<T>(response.data);
}

export async function post<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.post(url, data, config);
  return unwrapResponse<T>(response.data);
}

export async function patch<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.patch(url, data, config);
  return unwrapResponse<T>(response.data);
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.delete(url, config);
  return unwrapResponse<T>(response.data);
}

export default api;
